import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, Menu, X, ChevronDown, Baby } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { CONTACT_INFO, categories } from '../../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const { cartCount, openCart } = useCart();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setShowCategories(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Shop', path: '/shop', hasDropdown: true },
    { name: 'About', path: '/why-choose-us' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50" data-testid="site-header">
      {/* Top Announcement Bar */}
      <div className={`transition-all duration-500 ${isScrolled ? 'h-0 opacity-0 overflow-hidden' : 'h-10 opacity-100'}`}>
        <div className="h-full bg-athletic-500 flex items-center justify-center px-4">
          <p className="text-[11px] text-white tracking-[0.18em] uppercase">
            <span className="font-bold">Free Shipping</span> on orders $65+ &nbsp;•&nbsp; USA Only &nbsp;•&nbsp; {CONTACT_INFO.phone}
          </p>
        </div>
      </div>

      {/* Main Header */}
      <nav className={`transition-all duration-300 border-b ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-xl border-gray-100 py-3 shadow-sm'
          : 'bg-white border-gray-100 py-5'
      }`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between">

            {/* Left - Logo */}
            <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo">
              <div className="w-10 h-10 rounded-full bg-athletic-500/10 border border-athletic-500/30 flex items-center justify-center group-hover:bg-athletic-500/20 transition-colors">
                <Baby className="w-5 h-5 text-athletic-500" />
              </div>
              <div className="hidden sm:flex flex-col leading-tight">
                <span className="text-[#3a2a32] text-xl tracking-tight" style={{ fontFamily: "'Quicksand', sans-serif", fontWeight: 700 }}>
                  Tiny Wonders <span className="text-athletic-500">Pick</span>
                </span>
                <span className="text-[9px] font-medium uppercase tracking-[0.25em] text-[#3a2a32]/50">
                  Sweet Baby Essentials
                </span>
              </div>
              <span className="sm:hidden text-[#3a2a32] text-lg" style={{ fontFamily: "'Quicksand', sans-serif", fontWeight: 700 }}>
                Tiny Wonders <span className="text-athletic-500">Pick</span>
              </span>
            </Link>

            {/* Center - Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <div key={link.name} className="relative">
                  {link.hasDropdown ? (
                    <button
                      onClick={() => setShowCategories(!showCategories)}
                      data-testid={`nav-${link.name.toLowerCase()}`}
                      className={`flex items-center gap-1 px-4 py-2 text-sm tracking-wide transition-all ${
                        location.pathname.includes('/shop')
                          ? 'text-athletic-500 font-semibold'
                          : 'text-[#3a2a32]/70 hover:text-athletic-500'
                      }`}
                    >
                      {link.name}
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showCategories ? 'rotate-180' : ''}`} />
                    </button>
                  ) : (
                    <Link
                      to={link.path}
                      data-testid={`nav-${link.name.toLowerCase()}`}
                      className={`px-4 py-2 text-sm tracking-wide transition-all ${
                        location.pathname === link.path
                          ? 'text-athletic-500 font-semibold'
                          : 'text-[#3a2a32]/70 hover:text-athletic-500'
                      }`}
                    >
                      {link.name}
                    </Link>
                  )}

                  {link.hasDropdown && showCategories && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowCategories(false)} />
                      <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-2xl border border-gray-100 py-2 z-20 shadow-xl">
                        <Link
                          to="/shop"
                          className="block px-4 py-2.5 text-sm text-[#3a2a32] hover:text-athletic-500 hover:bg-athletic-50/40 font-semibold"
                          data-testid="nav-shop-all"
                        >
                          Shop All
                        </Link>
                        <div className="h-px bg-gray-100 my-1" />
                        {categories.map((cat) => (
                          <Link
                            key={cat.id}
                            to={`/shop?category=${cat.slug}`}
                            data-testid={`nav-cat-${cat.slug}`}
                            className="block px-4 py-2 text-sm text-[#3a2a32]/70 hover:text-athletic-500 hover:bg-athletic-50/40 transition-colors"
                          >
                            {cat.name}
                          </Link>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* Right - Actions */}
            <div className="hidden lg:flex items-center gap-1">
              {/* Cart */}
              <button
                onClick={openCart}
                className="relative ml-2 p-3 hover:bg-athletic-50/60 rounded-full text-[#3a2a32] transition-all group"
                data-testid="cart-button"
              >
                <ShoppingBag className="w-5 h-5 group-hover:scale-110 transition-transform" />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-athletic-500 text-white text-[10px] font-semibold flex items-center justify-center rounded-full">
                    {cartCount}
                  </span>
                )}
              </button>
            </div>

            {/* Mobile Menu */}
            <div className="flex lg:hidden items-center gap-3">
              <button
                onClick={openCart}
                className="relative p-2.5 hover:bg-athletic-50/60 rounded-full text-[#3a2a32]"
                data-testid="cart-button-mobile"
              >
                <ShoppingBag className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-athletic-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full">
                    {cartCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 text-[#3a2a32]"
                data-testid="mobile-menu-toggle"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Panel */}
        <div className={`lg:hidden transition-all duration-300 overflow-hidden ${
          isMobileMenuOpen ? 'max-h-[80vh] opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="bg-white border-t border-gray-100 px-6 py-6 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                className={`block px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'bg-athletic-50/60 text-athletic-500'
                    : 'text-[#3a2a32]/70 hover:text-[#3a2a32] hover:bg-gray-50'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <div className="pt-3 border-t border-gray-100 mt-3">
              <p className="px-4 text-[10px] uppercase tracking-[0.2em] text-[#3a2a32]/40 mb-2">Shop by category</p>
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/shop?category=${cat.slug}`}
                  className="block px-4 py-2.5 rounded-xl text-sm text-[#3a2a32]/70 hover:text-athletic-500 hover:bg-gray-50 transition-colors"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
